import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

/**
 * Composites the user's current design — and optionally their caption text —
 * on top of an empty Zazzle product image so the product card shows what
 * they'll actually receive, instead of the generic gray placeholder.
 *
 * Two render modes:
 *
 * 1. Flat (default): design is laid as-is inside a flat rectangle. Suitable
 *    for magnets, buttons, coasters, t-shirts, suitcases — anything that
 *    prints onto a flat surface.
 *
 * 2. Cylindrical (`wrap.type === 'cylindrical'`): design is sine-projected
 *    onto a virtual cylinder so it wraps around a cup/mug body. The horizontal
 *    axis of the design maps to angular position; columns near the cup edges
 *    get compressed (foreshortened) and parts beyond ±wrap.coverageDeg/2 wrap
 *    out of the visible front face. Caption + map ride together as a single
 *    image — the user's design PNG already contains both.
 *
 * `bounds` are expressed as percentages of the base image so the same
 * config works at any rendered size:
 *
 *   { x: 21, y: 28, w: 58, h: 36 }   // design starts 21% from the left,
 *                                    // 28% from the top, and covers 58%×36%
 *
 * `caption` (optional) positions a text label on the base image with
 * fontSize as a percentage of the rendered base height, so the rendered
 * label scales with the card.
 */

/**
 * Sine-projected ("cylindrical wrap") design layer.
 *
 * The design is conceptually wrapped around a vertical cylinder so that the
 * design's full width covers `wrap.coverageDeg` of the cylinder's
 * circumference. The viewer sees the front face (a 180° arc); anything past
 * ±90° on the cylinder wraps out of view to the sides.
 *
 * Per-column sine projection (horizontal):
 *   sinθ = x_out * sin(half-visible-angle)
 *   θ    = asin(sinθ)                  // angle on cylinder
 *   x_in = (θ + coverage/2) / coverage * srcWidth
 *
 * Per-column ellipse perspective (vertical):
 *   The cup is photographed from slightly above, so the rim and base circles
 *   project as ellipses — sides higher in the image, center lower. Every
 *   horizontal stripe on the cylinder follows the same ellipse, so we
 *   vertically offset each column's strip by `tilt * cos(θ)`. Sides
 *   (θ=±90°, cos=0) stay at the baseline; center (θ=0, cos=1) sinks down
 *   by `tilt`. Both top and bottom edges of the wrapped band shift together,
 *   matching how Zazzle's render shows the design curving along the rim.
 *
 * `wrap.tiltDeg` (default 0, ie no perspective) is the tilt angle in
 * degrees. The vertical drop in canvas pixels at the center column is
 * `sin(tilt) * R` where R is the cylinder's projected radius — i.e. roughly
 * `tan(tilt) * (canvasWidth/2)` for small tilts.
 */
const CylindricalDesignLayer = ({ designSrc, bounds, wrap, designBlendMode, designOpacity, baseHeight }) => {
    const canvasRef = useRef(null);
    const [img, setImg] = useState(null);

    useEffect(() => {
        if (!designSrc) {
            setImg(null);
            return undefined;
        }
        let cancelled = false;
        const el = new Image();
        // NOTE: no crossOrigin here. This canvas is DISPLAY-ONLY (never
        // toDataURL/getImageData'd), so a tainted canvas is fine — and setting
        // crossOrigin='anonymous' would make no-CORS images (e.g. Google Static
        // Maps previews on the Road Trips cup) fail to load entirely.
        el.onload = () => { if (!cancelled) setImg(el); };
        el.onerror = () => { if (!cancelled) setImg(null); };
        el.src = designSrc;
        return () => { cancelled = true; };
    }, [designSrc]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !img || !baseHeight) return;

        // Canvas pixel dimensions: match the displayed bounds rectangle so
        // we don't over- or under-sample. Multiply by devicePixelRatio for
        // crisp rendering on hi-DPI.
        const dpr = window.devicePixelRatio || 1;
        const heightPx = Math.max(1, Math.round((baseHeight * bounds.h) / 100));
        const widthPx = Math.max(1, Math.round((baseHeight * bounds.w) / 100));
        canvas.width = widthPx * dpr;
        canvas.height = heightPx * dpr;
        canvas.style.width = `${widthPx}px`;
        canvas.style.height = `${heightPx}px`;

        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // -- Wrap parameters (all optional with sensible defaults) --
        // Horizontal cylinder math:
        const coverageDeg = wrap.coverageDeg ?? 180;   // full angular range the design spans on the cylinder
        const offsetDeg   = wrap.offsetDeg   ?? 0;     // rotation of source center on the cylinder (negative = source-center moves right on screen)
        // Cylinder PROJECTED SIZE on screen — what the viewer actually sees.
        //   radiusPct      — cylinder radius (half of the visible front-face
        //                    width) as a fraction of the canvas width. The
        //                    visible front-face on screen is 2 * radiusPct
        //                    wide. Default null → auto from source aspect.
        //   bandHeightPct  — vertical height of the printable band as a
        //                    fraction of canvas height. Default null → auto
        //                    so the source aspect is preserved.
        // When BOTH are null, the wrap region is sized from source aspect
        // and coverage (object-fit:contain behaviour). Set either or both
        // to override and make the cup look fatter/skinnier or the print
        // taller/shorter independently of the source PNG's aspect.
        const radiusPct     = wrap.radiusPct     ?? null;
        const bandHeightPct = wrap.bandHeightPct ?? null;
        // Vertical (ellipse / camera tilt) math. tiltTopDeg / tiltBottomDeg
        // let you shape the top and bottom edges INDEPENDENTLY (top rim and
        // base have different visible curvature on a typical cup photo).
        // Single `tiltDeg` is a shorthand for setting both.
        const tiltTopDeg    = wrap.tiltTopDeg    ?? wrap.tiltDeg ?? 0;
        const tiltBottomDeg = wrap.tiltBottomDeg ?? wrap.tiltDeg ?? 0;
        // How the source's natural aspect fits the wrap canvas (only
        // relevant when radiusPct/bandHeightPct are NOT set — they
        // override fit when present):
        //   'contain' (default) — wrap region sized to source_aspect/α; centered in bounds.
        //   'fill'              — wrap region fills the full bounds rectangle (stretches design aspect).
        const fit = wrap.fit ?? 'contain';
        // Anchor the wrap region inside bounds when not filling. -1=top/left, 0=center, +1=bottom/right.
        const anchorX = wrap.anchorX ?? 0;
        const anchorY = wrap.anchorY ?? 0;
        // Multiplicative scale on the wrap region's natural width/height (1.0 = use full computed size).
        const widthScale  = wrap.widthScale  ?? 1.0;
        const heightScale = wrap.heightScale ?? 1.0;
        // Translate the wrap region inside bounds, as a fraction of bounds (positive y = down).
        const xShift = wrap.xShift ?? 0;
        const yShift = wrap.yShift ?? 0;
        // Crop a fraction of each source edge BEFORE wrapping. Useful when
        // the design source has white margin you don't want stretched onto
        // the cup. Values in [0, 1) where 0.05 = drop 5% off that edge.
        const cropTop    = wrap.sourceCrop?.top    ?? 0;
        const cropBottom = wrap.sourceCrop?.bottom ?? 0;
        const cropLeft   = wrap.sourceCrop?.left   ?? 0;
        const cropRight  = wrap.sourceCrop?.right  ?? 0;

        const halfCoverage = (coverageDeg / 2) * (Math.PI / 180);
        const offset = offsetDeg * (Math.PI / 180);
        const tiltTop = tiltTopDeg * (Math.PI / 180);
        const tiltBottom = tiltBottomDeg * (Math.PI / 180);
        // Visible front face spans -π/2 to +π/2; the projection scale is
        // sin(min(halfCoverage, π/2)) — anything beyond that maps off-screen.
        const halfVisible = Math.min(halfCoverage, Math.PI / 2);
        const sinHalfVisible = Math.sin(halfVisible);

        const canvasW = canvas.width;
        const canvasH = canvas.height;
        // Source rectangle after crop.
        const srcXStart = cropLeft * img.naturalWidth;
        const srcYStart = cropTop * img.naturalHeight;
        const srcW = img.naturalWidth * (1 - cropLeft - cropRight);
        const srcH = img.naturalHeight * (1 - cropTop - cropBottom);

        // The wrapped design's natural visible aspect is
        // source_aspect / halfCoverage_in_rad — flat width gets compressed
        // into the cylinder's visible front (2R = sourceWidth / α).
        const sourceAspect = srcW / srcH;
        const visibleAspect = sourceAspect / halfCoverage;
        const canvasAspect = canvasW / canvasH;
        let wrapW;
        let wrapH;
        // 1) Explicit cylinder dimensions take precedence over fit logic.
        if (radiusPct != null) {
            wrapW = 2 * radiusPct * canvasW;
        } else if (fit === 'fill') {
            wrapW = canvasW;
        } else if (visibleAspect >= canvasAspect) {
            wrapW = canvasW;
        } else {
            wrapW = canvasH * visibleAspect;
        }
        if (bandHeightPct != null) {
            wrapH = bandHeightPct * canvasH;
        } else if (fit === 'fill') {
            wrapH = canvasH;
        } else if (visibleAspect >= canvasAspect) {
            wrapH = wrapW / visibleAspect;
        } else {
            wrapH = canvasH;
        }
        wrapW *= widthScale;
        wrapH *= heightScale;

        // Per-edge tilt (in canvas px) — how far the CENTER column drops
        // relative to the side columns. Top and bottom move independently so
        // you can fit, say, a strong rim ellipse with a weak base curve.
        const tiltPxTop    = (wrapW / 2) * Math.sin(tiltTop);
        const tiltPxBottom = (wrapW / 2) * Math.sin(tiltBottom);
        // Reserve room for the tilt drops at top + bottom so the strip at
        // the center column doesn't overflow the canvas height. We anchor
        // by anchorX/Y (-1..+1) plus xShift/yShift offsets.
        const slack    = Math.max(tiltPxTop, tiltPxBottom);
        const freeX    = canvasW - wrapW;
        const freeY    = canvasH - wrapH - slack;
        const wrapOX   = Math.round(freeX * (anchorX + 1) / 2 + xShift * canvasW);
        const wrapOY   = Math.round(freeY * (anchorY + 1) / 2 + yShift * canvasH);

        // For each output column inside the wrap region, find the source
        // column to sample. Drawing 1-px wide strips lets the browser handle
        // vertical resampling for us (no manual bilinear loop).
        const wrapWInt = Math.round(wrapW);
        for (let xOut = 0; xOut < wrapWInt; xOut += 1) {
            const xn = (2 * xOut) / wrapW - 1;          // [-1, 1] across wrap region
            const sinTheta = xn * sinHalfVisible;
            if (sinTheta < -1 || sinTheta > 1) continue;
            const thetaVisible = Math.asin(sinTheta);
            const theta = thetaVisible + offset;
            // Map angle back to source column. Design's left edge is at
            // -halfCoverage on the cylinder; right edge at +halfCoverage.
            const u = (theta + halfCoverage) / (2 * halfCoverage);
            if (u < 0 || u >= 1) continue;
            const xSrcRel = u * srcW;       // x within cropped source
            // Source strip width: derivative of the inverse-sine map. The
            // wider the angular delta covered by this output pixel, the
            // wider the source slice we average over (anti-aliasing).
            const xn2 = (2 * (xOut + 1)) / wrapW - 1;
            const sinTheta2 = Math.max(-1, Math.min(1, xn2 * sinHalfVisible));
            const u2 = (Math.asin(sinTheta2) + offset + halfCoverage) / (2 * halfCoverage);
            const stripWidthSrc = Math.max(0.5, Math.abs(u2 - u) * srcW);
            // Independent top/bottom tilt offsets per column.
            // Top offset = tiltPxTop * cos(thetaVisible); bottom analogous.
            // We linearly interpolate within the strip so the strip's top
            // edge follows the top ellipse and its bottom edge follows the
            // bottom ellipse — drawn as a single drawImage by tweaking the
            // dst y and dst height per column.
            const cos = Math.cos(thetaVisible);
            const yOffsetTop    = tiltPxTop    * cos;
            const yOffsetBottom = tiltPxBottom * cos;
            const dstY = wrapOY + yOffsetTop;
            const dstH = wrapH + (yOffsetBottom - yOffsetTop);
            ctx.drawImage(
                img,
                srcXStart + xSrcRel, srcYStart, stripWidthSrc, srcH,
                wrapOX + xOut, dstY, 1, dstH,
            );
        }
    }, [
        img, bounds.w, bounds.h, baseHeight,
        wrap.coverageDeg, wrap.offsetDeg, wrap.tiltDeg,
        wrap.tiltTopDeg, wrap.tiltBottomDeg,
        wrap.radiusPct, wrap.bandHeightPct,
        wrap.fit, wrap.anchorX, wrap.anchorY,
        wrap.widthScale, wrap.heightScale,
        wrap.xShift, wrap.yShift,
        wrap.sourceCrop?.top, wrap.sourceCrop?.bottom,
        wrap.sourceCrop?.left, wrap.sourceCrop?.right,
    ]);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{
                position: 'absolute',
                left: `${bounds.x}%`,
                top: `${bounds.y}%`,
                width: `${bounds.w}%`,
                height: `${bounds.h}%`,
                mixBlendMode: designBlendMode || 'normal',
                opacity: designOpacity ?? 1,
                pointerEvents: 'none',
            }}
        />
    );
};

CylindricalDesignLayer.propTypes = {
    designSrc: PropTypes.string,
    bounds: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        w: PropTypes.number.isRequired,
        h: PropTypes.number.isRequired,
    }).isRequired,
    wrap: PropTypes.shape({
        type: PropTypes.string,
        coverageDeg: PropTypes.number,
        offsetDeg: PropTypes.number,
        tiltDeg: PropTypes.number,
    }).isRequired,
    designBlendMode: PropTypes.string,
    designOpacity: PropTypes.number,
    baseHeight: PropTypes.number,
};

/**
 * Caption text rendered onto the SAME cylinder as the design.
 *
 * Without this, the caption would be a flat HTML <div> sitting on top of
 * the wrapped design — that breaks the illusion. The bottle is curved, so
 * "50 US states visited" printed on it should curve too.
 *
 * Strategy: render the text into an off-screen canvas at flat layout,
 * then run the same sine-projection wrap as the design (same coverageDeg,
 * offsetDeg) so the caption follows the same cylinder. The output canvas
 * is positioned at the caption coordinates and shares the design's wrap
 * width so caption letters at the cylinder edges curve identically to
 * the design's pixels at the same angle.
 */
const CylindricalCaptionLayer = ({ captionText, caption, wrap, designBounds, baseHeight }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !baseHeight || !captionText) return undefined;

        const dpr = window.devicePixelRatio || 1;
        const fontPx = (baseHeight * (caption.fontSize || 3.2)) / 100;
        const fontFamily = caption.fontFamily || 'DongleApp, Helvetica, Arial, sans-serif';
        const fontWeight = caption.fontWeight || 700;
        const fontStr = `${fontWeight} ${fontPx}px ${fontFamily}`;
        const color = caption.color || '#000';

        // Canvas fillText silently falls back to a default font if the
        // requested family isn't fully loaded into the document's font set
        // when the canvas context first asks for it. The flat-overlay caption
        // works because the browser handles CSS font swapping for <div>
        // elements; canvas doesn't. We explicitly load the font BEFORE
        // drawing, then re-run the wrap once it's ready. Without this, the
        // wrapped caption renders in Helvetica even when DongleApp is loaded
        // for the rest of the page.
        let cancelled = false;
        const drawAfterFontLoad = async () => {
            try {
                if (document.fonts && document.fonts.load) {
                    await document.fonts.load(fontStr, captionText);
                }
            } catch { /* if font load fails, fall back to whatever's available */ }
            if (cancelled || !canvasRef.current) return;
            drawWrappedCaption();
        };

        const drawWrappedCaption = () => {
        // === The bug fix ===
        // Old behaviour: OUTPUT canvas width = text width. That made the
        // canvas extend wherever the text wanted to extend — past the
        // bottle body if the text was wider than the bottle. The user
        // (correctly) called this out: with proper cylinder math, the
        // text should NEVER go beyond the visible cylinder face.
        //
        // New behaviour: OUTPUT canvas width = the cylinder's visible
        // front-face width on screen (= 2R), derived from designBounds.w
        // (the design's wrap region). The caption is on the SAME cylinder
        // as the design, so it gets the same visible front. Anything wider
        // than that wraps to the back of the cylinder and disappears —
        // exactly what would happen on a real bottle.
        //
        // SOURCE canvas is sized to fit the text naturally (text width +
        // padding). When the text is narrower than the visible front, it
        // appears centered with the bottle body on either side. When wider,
        // the central portion fills the bottle and the edges curve away.
        const measureCtx = document.createElement('canvas').getContext('2d');
        measureCtx.font = fontStr;
        const textWidthPx = Math.max(1, measureCtx.measureText(captionText).width);
        const padPx = fontPx * 0.4;
        const srcCssW = textWidthPx + padPx * 2;
        // SOURCE canvas height: enough room for the text glyphs INCLUDING
        // descenders ("g", "y", "p" hang below baseline). 1.8 * fontPx is
        // a safe upper bound for most fonts.
        const srcCssH = fontPx * 1.8;
        // OUTPUT canvas width = design wrap region width on screen so the
        // caption sits on the SAME cylinder as the design. The caption can
        // never extend past this — if the text is naturally too wide, it
        // wraps off-cylinder rather than overflowing the bottle silhouette.
        const canvasCssW = (designBounds.w / 100) * baseHeight;
        const W = Math.max(2, Math.round(canvasCssW * dpr));
        const srcW = Math.max(2, Math.round(srcCssW * dpr));
        const srcH = Math.max(2, Math.round(srcCssH * dpr));

        // SOURCE: text drawn flat, centered horizontally + vertically.
        const srcCanvas = document.createElement('canvas');
        srcCanvas.width = srcW;
        srcCanvas.height = srcH;
        const srcCtx = srcCanvas.getContext('2d');
        srcCtx.scale(dpr, dpr);
        srcCtx.font = fontStr;
        srcCtx.fillStyle = color;
        srcCtx.textAlign = 'center';
        srcCtx.textBaseline = 'middle';
        srcCtx.fillText(captionText, srcCssW / 2, srcCssH / 2);

        // OUTPUT canvas needs EXTRA room below the source height to fit the
        // tilt-shifted strips. At the center column, the strip drops by
        // tiltPxBottom — without slack the canvas clips the descenders +
        // the whole bottom of the curve. We compute tilt magnitudes first
        // (they depend on W) then size the canvas height accordingly.
        const tiltTopDegEarly    = caption.tiltTopDeg    ?? caption.tiltDeg ?? wrap.tiltTopDeg    ?? wrap.tiltDeg ?? 0;
        const tiltBottomDegEarly = caption.tiltBottomDeg ?? caption.tiltDeg ?? wrap.tiltBottomDeg ?? wrap.tiltDeg ?? 0;
        const tiltSlackPx = Math.ceil((W / 2) * Math.max(
            Math.sin(tiltTopDegEarly * Math.PI / 180),
            Math.sin(tiltBottomDegEarly * Math.PI / 180),
        ));
        const canvasCssH = srcCssH + (tiltSlackPx / dpr);
        const H = srcH + tiltSlackPx;
        canvas.width = W;
        canvas.height = H;
        canvas.style.width = `${canvasCssW}px`;
        canvas.style.height = `${canvasCssH}px`;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, W, H);

        // The caption occupies a small angular range on the cylinder. The
        // angular range is determined by the text's natural width relative
        // to the visible front (= 2R). 100% width = 180° (text spans the
        // whole front); narrower text spans proportionally less.
        const ratio = Math.min(1, srcCssW / Math.max(canvasCssW, 1));
        const fallbackCoverage = 180 * ratio;
        const coverageDeg = caption.coverageDeg ?? fallbackCoverage;
        const offsetDeg   = caption.offsetDeg   ?? 0;
        // Caption inherits the design's vertical tilt by default so its
        // baseline follows the same ellipse curve as the design's top/bottom
        // edges. Without this, the text was just horizontally compressed —
        // visually almost flat for a short string. Override with
        // caption.tiltDeg / tiltTopDeg / tiltBottomDeg.
        const tiltTopDeg    = caption.tiltTopDeg
            ?? caption.tiltDeg
            ?? wrap.tiltTopDeg
            ?? wrap.tiltDeg
            ?? 0;
        const tiltBottomDeg = caption.tiltBottomDeg
            ?? caption.tiltDeg
            ?? wrap.tiltBottomDeg
            ?? wrap.tiltDeg
            ?? 0;
        const halfCoverage = (coverageDeg / 2) * (Math.PI / 180);
        const offset = offsetDeg * (Math.PI / 180);
        const tiltTop = tiltTopDeg * (Math.PI / 180);
        const tiltBottom = tiltBottomDeg * (Math.PI / 180);
        const halfVisible = Math.min(halfCoverage, Math.PI / 2);
        const sinHalfVisible = Math.sin(halfVisible);
        // Tilt magnitude in canvas pixels.
        const tiltPxTop    = (W / 2) * Math.sin(tiltTop);
        const tiltPxBottom = (W / 2) * Math.sin(tiltBottom);

        for (let xOut = 0; xOut < W; xOut += 1) {
            const xn = (2 * xOut) / W - 1;
            const sinTheta = xn * sinHalfVisible;
            if (sinTheta < -1 || sinTheta > 1) continue;
            const thetaVisible = Math.asin(sinTheta);
            const theta = thetaVisible + offset;
            const u = (theta + halfCoverage) / (2 * halfCoverage);
            // u outside [0, 1) means this canvas column is BEYOND where the
            // text wraps onto the cylinder — leave it transparent so the
            // bottle body shows through. This is the key behaviour: text
            // never extends past the cylinder's visible front.
            if (u < 0 || u >= 1) continue;
            // Sample source by source-pixel coordinates (srcW), not canvas
            // pixels (W). Fixed bug where the loop used W for both.
            const xSrc = u * srcW;
            const xn2 = (2 * (xOut + 1)) / W - 1;
            const sinTheta2 = Math.max(-1, Math.min(1, xn2 * sinHalfVisible));
            const u2 = (Math.asin(sinTheta2) + offset + halfCoverage) / (2 * halfCoverage);
            const stripWidthSrc = Math.max(0.5, Math.abs(u2 - u) * srcW);
            // Vertical curve: same per-column ellipse offset as the design
            // wrap. cos(thetaVisible) is 1 at canvas center (column drops by
            // tiltPx) and 0 at edges (column sits at top).
            const cos = Math.cos(thetaVisible);
            const yOffsetTop    = tiltPxTop    * cos;
            const yOffsetBottom = tiltPxBottom * cos;
            const dstY = yOffsetTop;
            const dstH = srcH + (yOffsetBottom - yOffsetTop);
            // Source height is srcH (just the text), destination scales to dstH.
            ctx.drawImage(srcCanvas, xSrc, 0, stripWidthSrc, srcH, xOut, dstY, 1, dstH);
        }
        };
        drawAfterFontLoad();
        return () => { cancelled = true; };
    }, [
        captionText,
        caption.fontSize, caption.fontWeight, caption.fontFamily, caption.color,
        caption.coverageDeg, caption.offsetDeg,
        caption.tiltDeg, caption.tiltTopDeg, caption.tiltBottomDeg,
        wrap.coverageDeg, wrap.tiltDeg, wrap.tiltTopDeg, wrap.tiltBottomDeg,
        designBounds.w,
        baseHeight,
    ]);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{
                position: 'absolute',
                left: `${caption.x}%`,
                top: `${caption.y}%`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
            }}
        />
    );
};

CylindricalCaptionLayer.propTypes = {
    captionText: PropTypes.string,
    caption: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        fontSize: PropTypes.number,
        color: PropTypes.string,
        fontFamily: PropTypes.string,
        fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        maxWidth: PropTypes.number,
    }).isRequired,
    wrap: PropTypes.shape({
        type: PropTypes.string,
        coverageDeg: PropTypes.number,
        offsetDeg: PropTypes.number,
    }).isRequired,
    designBounds: PropTypes.shape({
        w: PropTypes.number.isRequired,
    }).isRequired,
    baseHeight: PropTypes.number,
};

const LivePreview = ({ baseSrc, designSrc, bounds, captionText, caption, alt, designFit = 'contain', designPosition = 'center', designBlendMode = 'normal', designOpacity = 1, wrap }) => {
    // Track the rendered base-image height so caption sizing scales with it
    // (fontSize is expressed as a % of the rendered base height in the config).
    const containerRef = useRef(null);
    const [boxHeight, setBoxHeight] = useState(0);

    useEffect(() => {
        if (!containerRef.current) return undefined;
        const update = () => {
            if (containerRef.current) {
                setBoxHeight(containerRef.current.clientHeight);
            }
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    const showCaption = !!(captionText && caption);
    const captionFontPx = showCaption
        ? Math.max(8, (boxHeight * (caption.fontSize || 3.2)) / 100)
        : 0;

    // The design + caption overlays mount only once designSrc is present.
    // Wrapping them in a single fade-in container means the empty cup stays
    // static and only the user's design "settles" onto it — much calmer than
    // swapping product photos under the user's nose.
    const hasOverlay = !!(designSrc && bounds);
    return (
        <Box ref={containerRef} sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <img
                src={baseSrc}
                alt={alt}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                }}
            />
            {hasOverlay && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        animation: 'liveDesignFadeIn 420ms ease-out',
                        '@keyframes liveDesignFadeIn': {
                            from: { opacity: 0, transform: 'scale(0.985)' },
                            to: { opacity: 1, transform: 'scale(1)' },
                        },
                    }}
                >
                {wrap && wrap.type === 'cylindrical' ? (
                    <CylindricalDesignLayer
                        designSrc={designSrc}
                        bounds={bounds}
                        wrap={wrap}
                        designBlendMode={designBlendMode}
                        designOpacity={designOpacity}
                        baseHeight={boxHeight}
                    />
                ) : (
                    <img
                        src={designSrc}
                        alt=""
                        aria-hidden="true"
                        style={{
                            position: 'absolute',
                            left: `${bounds.x}%`,
                            top: `${bounds.y}%`,
                            width: `${bounds.w}%`,
                            height: `${bounds.h}%`,
                            objectFit: designFit,
                            objectPosition: designPosition,
                            mixBlendMode: designBlendMode,
                            opacity: designOpacity,
                            pointerEvents: 'none',
                        }}
                    />
                )}
                {showCaption && wrap && wrap.type === 'cylindrical' && (
                    // Caption ALSO wraps around the cylinder — without this it'd
                    // be a flat HTML <div> stuck on top of the wrapped design,
                    // breaking the 3D illusion.
                    <CylindricalCaptionLayer
                        captionText={captionText}
                        caption={caption}
                        wrap={wrap}
                        designBounds={bounds}
                        baseHeight={boxHeight}
                    />
                )}
                {showCaption && !(wrap && wrap.type === 'cylindrical') && (
                // Plain div with inline style — using MUI Box sx caused the
                // fontFamily to fall through to the inherited Plus Jakarta
                // Sans (Emotion's CSS-in-JS ordering can lose to component
                // theme defaults). Inline style wins the cascade.
                <div
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        left: `${caption.x}%`,
                        top: `${caption.y}%`,
                        transform: 'translate(-50%, -50%)',
                        maxWidth: `${caption.maxWidth || 70}%`,
                        textAlign: 'center',
                        color: caption.color || '#000',
                        fontFamily: caption.fontFamily || 'DongleApp, Helvetica, Arial, sans-serif',
                        fontWeight: caption.fontWeight || 700,
                        fontSize: `${captionFontPx}px`,
                        lineHeight: 1.1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                >
                    {captionText}
                </div>
                )}
                </Box>
            )}
        </Box>
    );
};

LivePreview.propTypes = {
    baseSrc: PropTypes.string.isRequired,
    designSrc: PropTypes.string,
    bounds: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        w: PropTypes.number.isRequired,
        h: PropTypes.number.isRequired,
    }),
    captionText: PropTypes.string,
    caption: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        fontSize: PropTypes.number,
        color: PropTypes.string,
        fontFamily: PropTypes.string,
        fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        maxWidth: PropTypes.number,
    }),
    alt: PropTypes.string,
    designFit: PropTypes.oneOf(['contain', 'cover', 'fill']),
    designPosition: PropTypes.string,
    designBlendMode: PropTypes.string,
    designOpacity: PropTypes.number,
    wrap: PropTypes.shape({
        type: PropTypes.string.isRequired,
        coverageDeg: PropTypes.number,
        offsetDeg: PropTypes.number,
        tiltDeg: PropTypes.number,
    }),
};

export default LivePreview;
